/**
 * Shared serialization + normalization + Cache B helpers for the authenticated
 * member's theme preferences.
 *
 * ONE JSON naming contract (see `_docs/architecture/theme-system.md` §6):
 *   client / runtime model : camelCase   { themeMode, workspaceDefaults: { newWorkspaceCanvasMode, savedCanvasDefault } }
 *   persisted API JSON     : snake_case  { appearance: { theme_mode }, workspace_defaults: { new_workspace_canvas_mode, saved_canvas_default } }
 *
 * This module is the ONLY translator. Cache B is a member-scoped, server-load-
 * failure fallback owned exclusively by `useAppearanceSettings` — never read by
 * `useTheme`, never keyed globally, never keyed by another user's id.
 */

import { normalizeThemeMode } from './themePreference';
import {
  DEFAULT_NEW_WORKSPACE_CANVAS_MODE,
  normalizeWorkspaceDefaults,
} from './workspaceNewCanvasDefault';

export const MEMBER_APPEARANCE_CACHE_PREFIX = 'skyspeaks.appearanceSettings.';

/** Default client-model appearance settings (used when nothing has loaded). */
export function defaultAppearanceSettings() {
  return {
    themeMode: 'system',
    workspaceDefaults: {
      newWorkspaceCanvasMode: DEFAULT_NEW_WORKSPACE_CANVAS_MODE,
      savedCanvasDefault: null,
    },
  };
}

/**
 * Server `preferences` JSON (snake_case) -> client model (camelCase). Missing /
 * malformed groups fall back to normalized defaults. Never throws.
 */
export function deserializePreferences(preferences) {
  const p = preferences != null && typeof preferences === 'object' ? preferences : {};
  const appearance = p.appearance != null && typeof p.appearance === 'object' ? p.appearance : {};
  const wd = p.workspace_defaults != null && typeof p.workspace_defaults === 'object'
    ? p.workspace_defaults
    : {};
  return {
    themeMode: normalizeThemeMode(appearance.theme_mode),
    workspaceDefaults: normalizeWorkspaceDefaults({
      newWorkspaceCanvasMode: wd.new_workspace_canvas_mode,
      savedCanvasDefault: wd.saved_canvas_default,
    }),
  };
}

/** `{ preferences: { appearance: { theme_mode } } }` PATCH body for a theme-mode change. */
export function serializeThemeModePatch(themeMode) {
  return { preferences: { appearance: { theme_mode: normalizeThemeMode(themeMode) } } };
}

/** `{ preferences: { workspace_defaults: { new_workspace_canvas_mode } } }` PATCH body. */
export function serializeNewWorkspaceCanvasModePatch(mode) {
  return {
    preferences: {
      workspace_defaults: {
        new_workspace_canvas_mode: normalizeWorkspaceDefaults({ newWorkspaceCanvasMode: mode })
          .newWorkspaceCanvasMode,
      },
    },
  };
}

/**
 * `{ preferences: { workspace_defaults: { saved_canvas_default } } }` PATCH body.
 * `null` is an explicit clear (legal for this field only).
 */
export function serializeSavedCanvasDefaultPatch(bundle) {
  return { preferences: { workspace_defaults: { saved_canvas_default: bundle ?? null } } };
}

// -- Cache B ---------------------------------------------------------------

export function memberAppearanceCacheKey(userId) {
  return `${MEMBER_APPEARANCE_CACHE_PREFIX}${userId}`;
}

/**
 * Read this member's Cache B. Malformed JSON -> ignored (null). Malformed fields
 * are normalized. Never throws, never returns another user's data.
 */
export function readMemberAppearanceCache(userId) {
  if (userId == null) return null;
  try {
    const raw = localStorage.getItem(memberAppearanceCacheKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed == null || typeof parsed !== 'object') return null;
    return {
      themeMode: normalizeThemeMode(parsed.themeMode),
      workspaceDefaults: normalizeWorkspaceDefaults(parsed.workspaceDefaults),
    };
  } catch {
    return null;
  }
}

/** Write normalized client-model data to this member's Cache B. Never throws. */
export function writeMemberAppearanceCache(userId, normalized) {
  if (userId == null) return;
  try {
    const payload = {
      themeMode: normalizeThemeMode(normalized && normalized.themeMode),
      workspaceDefaults: normalizeWorkspaceDefaults(normalized && normalized.workspaceDefaults),
    };
    localStorage.setItem(memberAppearanceCacheKey(userId), JSON.stringify(payload));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Remove this member's Cache B row (used on logout for the just-departed member). */
export function clearMemberAppearanceCache(userId) {
  if (userId == null) return;
  try {
    localStorage.removeItem(memberAppearanceCacheKey(userId));
  } catch {
    /* ignore */
  }
}
